import { useEffect, useState } from "react";
import { BusyLabel } from "@/components/loading";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AlertTriangle } from "lucide-react";

// Reusable dialog for admin actions that require a categorized reason.
// Mirrors the dispute-resolve UX: fixed presets + "Otro" free text.
// The email sent to the affected user contains the exact rendered text
// (preset label or the custom text), never a canned template.
export function ReasonPickerDialog({
  open,
  onOpenChange,
  title,
  description,
  presets,             // [{ value: 'fraud', label: 'Fraude o estafa' }, ...]
  submitLabel = "Confirmar",
  destructive = false,
  extraContext,        // node rendered above the picker (e.g., previous ban reason)
  onSubmit,            // (finalReason: string, presetValue: string | null) => Promise<void>
}) {
  const [choice, setChoice] = useState("");
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) { setChoice(""); setCustom(""); setLoading(false); }
  }, [open]);

  const isOtro = choice === "otro";
  const canSubmit = !!choice && (!isOtro || custom.trim().length >= 3);

  const submit = async () => {
    if (!canSubmit) return;
    const preset = presets.find((p) => p.value === choice);
    const finalReason = isOtro ? custom.trim() : (preset?.label || "");
    if (!finalReason || finalReason.length < 3) return;
    setLoading(true);
    try {
      await onSubmit(finalReason, isOtro ? null : choice);
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111] border-[#262626] text-white max-w-lg" data-testid="reason-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            {destructive && <AlertTriangle size={20} className="text-[#EF4444]" />}
            {title}
          </DialogTitle>
        </DialogHeader>
        {description && <p className="text-sm text-neutral-400">{description}</p>}
        {extraContext}
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-neutral-400">Elige un motivo</Label>
          <div className="space-y-1.5" role="radiogroup" data-testid="reason-picker-options">
            {presets.map((p) => (
              <label
                key={p.value}
                className={`flex items-start gap-2 p-3 rounded border cursor-pointer transition ${
                  choice === p.value
                    ? destructive ? "border-[#EF4444] bg-red-500/10" : "border-emerald-500 bg-emerald-500/10"
                    : "border-[#262626] hover:border-neutral-600"
                }`}
              >
                <input
                  type="radio"
                  name="reason-preset"
                  value={p.value}
                  checked={choice === p.value}
                  onChange={() => setChoice(p.value)}
                  className="mt-1 accent-[#EF4444]"
                  data-testid={`reason-preset-${p.value}`}
                />
                <div className="flex-1">
                  <div className="text-sm text-white">{p.label}</div>
                  {p.hint && <div className="text-xs text-neutral-500 mt-0.5">{p.hint}</div>}
                </div>
              </label>
            ))}
          </div>
        </div>
        {isOtro && (
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-neutral-400">Motivo personalizado (mín 3 caracteres)</Label>
            <Textarea
              data-testid="reason-picker-custom"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="Describe el motivo — este texto exacto le llega por correo al usuario."
              className="bg-[#171717] border-[#262626] text-white min-h-[100px]"
              maxLength={500}
            />
            <div className="text-xs text-neutral-500 text-right">{custom.length}/500</div>
          </div>
        )}
        <p className="text-[11px] text-neutral-500">
          El motivo elegido se envía por correo al usuario tal cual — sé claro y respetuoso.
        </p>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="reason-picker-cancel">Cancelar</Button>
          <Button
            data-testid="reason-picker-submit"
            onClick={submit}
            disabled={!canSubmit || loading}
            className={destructive ? "bg-[#EF4444] hover:bg-[#DC2626]" : "bg-emerald-600 hover:bg-emerald-700"}
          >
            <BusyLabel busy={loading} text="Enviando…">{submitLabel}</BusyLabel>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Preset lists (single source of truth so backend audit + emails match UI copy).
export const BAN_REASONS = [
  { value: "fraude", label: "Fraude o intento de estafa", hint: "Uso indebido de pagos o suplantación de identidad." },
  { value: "cartas_falsas", label: "Cartas falsas o publicación engañosa", hint: "Fotos manipuladas, condición mentida o cartas alteradas." },
  { value: "no_envio", label: "No envío reiterado", hint: "Vendió y no despachó en el plazo, con reincidencia." },
  { value: "abuso", label: "Comportamiento abusivo o insultos", hint: "Trato irrespetuoso a compradores, vendedores o soporte." },
  { value: "cuenta_duplicada", label: "Cuenta duplicada o cédula ya baneada", hint: "Elude una sanción con otra cuenta." },
  { value: "otro", label: "Otro (escribir motivo personalizado)" },
];

export const UNBAN_REASONS = [
  { value: "falso_positivo", label: "Falso positivo — la sanción fue un error", hint: "Se comprobó que el usuario no cometió la falta." },
  { value: "aclarado", label: "Situación aclarada con el usuario", hint: "Se resolvió el motivo original y no hubo mala fe." },
  { value: "sancion_cumplida", label: "Sanción cumplida — segunda oportunidad", hint: "Ya pasó el tiempo/pago acordado y se otorga acceso." },
  { value: "evidencia_nueva", label: "Nueva evidencia exculpa al usuario", hint: "Apareció información que absuelve al usuario." },
  { value: "otro", label: "Otro (escribir motivo personalizado)" },
];
